import { useMemo } from "react"
import { LeaderboardEntry } from "@mezo/api"
import {
  Block,
  LabelMedium,
  ListItem,
  ParagraphMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { separateThousandsWithComaRegExp, trimAddress } from "#/shared/utils"

type LeaderboardItemProps = Omit<LeaderboardEntry, "invitedBy"> & {
  isCurrentUser: boolean
  showPositionChange: boolean
  positionChange: number
  rank: number
}

export default function LeaderboardItem({
  username,
  score,
  address,
  rank,
  isCurrentUser,
  showPositionChange,
  positionChange,
}: LeaderboardItemProps) {
  const [css, theme] = useStyletron()

  const highlight = useMemo(() => {
    switch (rank) {
      case 1:
        return theme.colors.green100
      case 2:
        return theme.colors.yellow300
      case 3:
        return theme.colors.blue300
      default:
        return null
    }
  }, [rank, theme.colors])

  const formattedUsername = username ?? trimAddress(address)

  const contentColor = useMemo(
    () => (isCurrentUser ? theme.colors.white : theme.colors.black),
    [isCurrentUser, theme.colors],
  )

  const scoreColor = useMemo(() => {
    if (isCurrentUser) return theme.colors.white

    if (showPositionChange && positionChange > 0)
      return theme.colors.contentPositive

    if (showPositionChange && positionChange < 0)
      return theme.colors.contentNegative

    return theme.colors.black
  }, [isCurrentUser, positionChange, showPositionChange, theme.colors])

  const scoreLabel = useMemo(() => {
    if (!showPositionChange)
      return separateThousandsWithComaRegExp(score.toString())

    if (positionChange === 0) return "-"

    if (positionChange > 0) return `+${positionChange}`

    return positionChange
  }, [positionChange, score, showPositionChange])

  return (
    <ListItem
      overrides={{
        Root: {
          style: () => ({
            background: isCurrentUser ? theme.colors.black : highlight,
          }),
        },
        Content: {
          style: () => ({
            marginLeft: 0,
            paddingLeft: theme.sizing.scale600,
          }),
        },
      }}
    >
      <ParagraphMedium color={contentColor} width={theme.sizing.scale1600}>
        #{rank}
      </ParagraphMedium>
      <LabelMedium flex={1} color={contentColor}>
        {formattedUsername}
      </LabelMedium>

      <Block
        className={css({
          width: showPositionChange ? "auto" : theme.sizing.scale3200,
          display: "flex",
          alignItems: "center",
          gap: theme.sizing.scale400,
        })}
      >
        <LabelMedium
          color={scoreColor}
          className={css({
            width: showPositionChange ? "auto" : theme.sizing.scale3200,
            textAlign: "right",
          })}
        >
          {scoreLabel}
        </LabelMedium>
      </Block>
    </ListItem>
  )
}
